import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import api from '@/lib/api';
import { toast } from 'sonner';
import { useTranslation } from '@/contexts/LanguageContext';
import SEO from '@/components/SEO';
import { Phone, Truck, Camera, Rocket, CheckCircle, Loader2 } from 'lucide-react';

const STEPS = [
  { icon: Phone, titleKey: 'step1Title', descKey: 'step1Desc' },
  { icon: Truck, titleKey: 'step2Title', descKey: 'step2Desc' },
  { icon: Camera, titleKey: 'step3Title', descKey: 'step3Desc' },
  { icon: Rocket, titleKey: 'step4Title', descKey: 'step4Desc' },
];

const TARGETS = ['targetItem1', 'targetItem2', 'targetItem3'];

export default function ServicePublicationPage() {
  const navigate = useNavigate();
  const { t } = useTranslation('servicePublication');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({
    first_last_name: '', company: '', email: '', phone: '', location: '', volume_estimate: '', message: ''
  });

  const update = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post('/service-publication/request', form);
      toast.success(t('successToast'));
      setSent(true);
      setForm({ first_last_name: '', company: '', email: '', phone: '', location: '', volume_estimate: '', message: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || t('errorToast'));
    } finally { setSending(false); }
  };

  return (
    <div data-testid="service-publication-page">
      <SEO
        title="Service clés en main — Nous publions vos annonces"
        description="Vous avez du stock à écouler ? DestockPro s'occupe de tout : photos, rédaction et mise en ligne de vos annonces de déstockage professionnel. Simple, rapide et efficace."
        path="/service-publication"
      />

      {/* ═══ Hero ═══ */}
      <section className="bg-[#1F3F72] py-10 lg:py-14">
        <div className="site-container text-center">
          <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4" data-testid="sp-hero-title">{t('heroTitle')}</h1>
          <p className="text-base sm:text-lg text-white/90 max-w-2xl mx-auto">{t('heroDesc')}</p>
        </div>
      </section>

      {/* ═══ How it works ═══ */}
      <section className="py-14 lg:py-18 bg-slate-900">
        <div className="site-container">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white text-center mb-10">{t('howTitle')}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {STEPS.map((step, i) => (
              <div key={step.titleKey} className="text-center" data-testid={`sp-step-${i + 1}`}>
                <div className="inline-flex items-center justify-center w-14 h-14 rounded-xl bg-[#F25C05]/15 mb-3">
                  <step.icon className="w-7 h-7 text-[#F25C05]" />
                </div>
                <div className="text-xs font-bold text-white/30 mb-1">{i + 1}</div>
                <h3 className="font-heading text-base font-semibold text-white mb-1">{t(step.titleKey)}</h3>
                <p className="text-sm text-slate-400">{t(step.descKey)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ Pour qui ═══ */}
      <section className="py-12 lg:py-16 bg-neutral-50">
        <div className="site-container">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-neutral-900 text-center mb-8">{t('targetTitle')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-4xl mx-auto">
            {TARGETS.map(k => (
              <div key={k} className="flex items-start gap-3 bg-white rounded-xl border p-4">
                <CheckCircle className="w-5 h-5 text-[#F25C05] shrink-0 mt-0.5" />
                <p className="text-sm text-neutral-700">{t(k)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ Tarif ═══ */}
      <section className="py-12 lg:py-14 bg-[#1F3F72]">
        <div className="site-container text-center">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-4">{t('pricingTitle')}</h2>
          <p className="text-base text-white/90 mb-2">{t('pricingDesc')}</p>
          <p className="text-sm text-blue-200">{t('pricingDelay')}</p>
        </div>
      </section>

      {/* ═══ Formulaire ═══ */}
      <section className="py-14 lg:py-18 bg-slate-900" data-testid="sp-form-section">
        <div className="site-container max-w-2xl">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white text-center mb-2">{t('formTitle')}</h2>
          <p className="text-sm text-slate-400 text-center mb-8">{t('formSubtitle')}</p>

          {sent ? (
            <div className="text-center py-12" data-testid="sp-form-success">
              <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
              <p className="text-lg font-semibold text-white mb-2">{t('successTitle')}</p>
              <p className="text-sm text-slate-400 mb-6">{t('successDesc')}</p>
              <Button variant="outline" className="border-white/30 text-white hover:bg-white/10" onClick={() => navigate('/')}>{t('backHome')}</Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-1.5">{t('fieldName')}</label>
                  <Input required value={form.first_last_name} onChange={e => update('first_last_name', e.target.value)} className="bg-slate-800 border-slate-700 text-white" data-testid="sp-name" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-1.5">{t('fieldCompany')}</label>
                  <Input required value={form.company} onChange={e => update('company', e.target.value)} className="bg-slate-800 border-slate-700 text-white" data-testid="sp-company" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-1.5">{t('fieldEmail')}</label>
                  <Input type="email" required value={form.email} onChange={e => update('email', e.target.value)} className="bg-slate-800 border-slate-700 text-white" data-testid="sp-email" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-1.5">{t('fieldPhone')}</label>
                  <Input type="tel" required value={form.phone} onChange={e => update('phone', e.target.value)} className="bg-slate-800 border-slate-700 text-white" data-testid="sp-phone" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-1.5">{t('fieldLocation')}</label>
                  <Input required value={form.location} onChange={e => update('location', e.target.value)} placeholder={t('fieldLocationPlaceholder')} className="bg-slate-800 border-slate-700 text-white" data-testid="sp-location" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-400 mb-1.5">{t('fieldVolume')}</label>
                  <Input required value={form.volume_estimate} onChange={e => update('volume_estimate', e.target.value)} placeholder={t('fieldVolumePlaceholder')} className="bg-slate-800 border-slate-700 text-white" data-testid="sp-volume" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-400 mb-1.5">{t('fieldMessage')}</label>
                <textarea value={form.message} onChange={e => update('message', e.target.value)} placeholder={t('fieldMessagePlaceholder')} rows={3} className="w-full rounded-md bg-slate-800 border border-slate-700 text-white text-sm px-3 py-2 resize-y" data-testid="sp-message" />
              </div>
              <Button type="submit" disabled={sending} className="w-full sm:w-auto bg-[#F25C05] hover:bg-[#d94f04] text-white font-semibold h-12 px-8" data-testid="sp-submit">
                {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                {t('submitBtn')}
              </Button>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
